import type { ManagerRow } from './types'

export interface ManagersTableProps {
  managers: ManagerRow[]
  onRowClick?: (id: string) => void
  onShowAll?: () => void
}

function formatRub(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${Math.round(n)}`
}

function timeToKpClass(days: number | null): string {
  if (days === null) return 'text-gray-400'
  if (days < 2) return 'text-emerald-600 font-medium'
  if (days <= 4) return 'text-gray-700'
  return 'text-red-600 font-medium'
}

function conversionClass(pct: number | null): string {
  if (pct === null) return 'text-gray-400'
  if (pct >= 35) return 'text-emerald-700 bg-emerald-50'
  if (pct >= 20) return 'text-amber-700 bg-amber-50'
  return 'text-red-700 bg-red-50'
}

export function ManagersTable({ managers, onRowClick, onShowAll }: ManagersTableProps) {
  const top = [...managers].sort((a, b) => b.kpSumRub - a.kpSumRub).slice(0, 5)

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-500">Менеджеры</div>
        {onShowAll ? (
          <button
            type="button"
            onClick={onShowAll}
            className="text-xs text-brand-600 hover:text-brand-700 hover:underline"
          >
            Все {managers.length} →
          </button>
        ) : (
          <span className="text-xs text-gray-400">Все {managers.length}</span>
        )}
      </div>

      {top.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">Нет данных за период</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500">
              <th className="text-left font-normal pb-2">Имя</th>
              <th className="text-right font-normal pb-2">Заявки</th>
              <th className="text-right font-normal pb-2">Из них с КП</th>
              <th className="text-right font-normal pb-2">КП ₽</th>
              <th className="text-right font-normal pb-2">Ср. до КП</th>
              <th className="text-right font-normal pb-2">Конв.</th>
            </tr>
          </thead>
          <tbody>
            {top.map((m) => (
              <tr
                key={m.id}
                onClick={() => onRowClick?.(m.id)}
                className={`border-t border-gray-100 ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              >
                <td className="py-2 text-gray-900">{m.name}</td>
                <td className="py-2 text-right tabular-nums">{m.requestsCount}</td>
                <td className="py-2 text-right tabular-nums text-gray-600">
                  {m.requestsWithKpCount}
                </td>
                <td className="py-2 text-right tabular-nums text-gray-700">
                  {formatRub(m.kpSumRub)}
                </td>
                <td className={`py-2 text-right tabular-nums ${timeToKpClass(m.avgTimeToKpDays)}`}>
                  {m.avgTimeToKpDays === null ? '—' : `${m.avgTimeToKpDays}д`}
                </td>
                <td className="py-2 text-right">
                  {m.conversionPercent === null ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-xs tabular-nums ${conversionClass(m.conversionPercent)}`}
                    >
                      {m.conversionPercent}%
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
